import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { obtenerEspecificacionesPorId, actualizarEspecificaciones } from './api/catalogo';
import { ArrowLeft, Save, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-toastify';

// ── Helpers ───────────────────────────────────────────────────────────────────
const deepClone = v => JSON.parse(JSON.stringify(v ?? {}));
const isArray = v => Array.isArray(v);
const isObject = v => v !== null && typeof v === 'object' && !Array.isArray(v);

// Accesores seguros para estructura dual (raíz + vehiculo.*)
const getMarca = d => d.vehiculo?.marca || d.marca || '';
const getModelo = d => d.vehiculo?.modelo || d.modelo || '';
const getVersion = d => d.vehiculo?.version || d.version || '';

// Secciones conocidas dentro de specs.*
const SECCIONES_SPECS = [
    { key: 'motor', label: 'Motor' },
    { key: 'suspension_frenos', label: 'Suspensión y frenos' },
    { key: 'dimensiones', label: 'Dimensiones' },
    { key: 'direccion', label: 'Dirección' },
    { key: 'comparativas', label: 'Comparativas (rendimiento, capacidades…)' },
    { key: 'equipamiento_estandar', label: 'Equipamiento estándar' },
    { key: 'equipamiento_diferencial', label: 'Equipamiento diferencial' },
    { key: 'colores', label: 'Colores disponibles' },
];

// ── Editor de valor hoja ──────────────────────────────────────────────────────
function LeafEditor({ value, onChange }) {
    return (
        <input
            value={value ?? ''}
            onChange={e => onChange(e.target.value)}
            style={{ width: '100%', fontSize: 13, padding: '5px 8px', boxSizing: 'border-box' }}
        />
    );
}

// ── Editor de array de strings ────────────────────────────────────────────────
function ArrayEditor({ value = [], onChange }) {
    // Detectar si es array de objetos {caracteristica, valor} (formato Kia/diferencial)
    const isObjArray = value.length > 0 && isObject(value[0]);

    if (isObjArray) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {value.map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            value={item.caracteristica || ''}
                            onChange={e => {
                                const a = [...value];
                                a[i] = { ...a[i], caracteristica: e.target.value };
                                onChange(a);
                            }}
                            placeholder="Característica"
                            style={{ flex: 3, fontSize: 13, padding: '4px 8px' }}
                        />
                        <select
                            value={String(item.valor)}
                            onChange={e => {
                                const a = [...value];
                                a[i] = { ...a[i], valor: e.target.value === 'true' };
                                onChange(a);
                            }}
                            style={{ flex: 1, fontSize: 13, padding: '4px 6px' }}
                        >
                            <option value="true">✅ Sí</option>
                            <option value="false">❌ No</option>
                        </select>
                        <button
                            onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                            style={{ padding: '4px 8px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-danger)' }}
                        >
                            <Trash2 size={13} />
                        </button>
                    </div>
                ))}
                <button
                    onClick={() => onChange([...value, { caracteristica: '', valor: true }])}
                    style={{ alignSelf: 'flex-start', fontSize: 12, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                    <Plus size={12} /> Agregar ítem
                </button>
            </div>
        );
    }

    // Array de strings simple
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {value.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input
                        value={item}
                        onChange={e => { const a = [...value]; a[i] = e.target.value; onChange(a); }}
                        style={{ flex: 1, fontSize: 13, padding: '4px 8px' }}
                    />
                    <button
                        onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                        style={{ padding: '4px 8px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
                    >
                        <Trash2 size={13} />
                    </button>
                </div>
            ))}
            <button
                onClick={() => onChange([...value, ''])}
                style={{ alignSelf: 'flex-start', fontSize: 12, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
            >
                <Plus size={12} /> Agregar ítem
            </button>
        </div>
    );
}

// ── Editor recursivo de mapa ──────────────────────────────────────────────────
function MapEditor({ data, path, onChange }) {
    const [collapsed, setCollapsed] = useState({});
    const toggle = key => setCollapsed(p => ({ ...p, [key]: !p[key] }));
    const removeKey = key => {
        if (!window.confirm(`¿Eliminar el campo "${key}"?`)) return;
        onChange([...path, key], undefined);
    };
    const addKey = () => {
        const key = prompt('Nombre del nuevo campo:');
        if (!key?.trim()) return;
        onChange([...path, key.trim()], '');
    };

    if (!data || typeof data !== 'object') return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Object.entries(data).map(([key, value]) => {
                const nested = isObject(value);
                const arrVal = isArray(value);
                const isCol = collapsed[key];

                return (
                    <div key={key} style={{ border: '0.5px solid var(--color-border-tertiary)', borderRadius: 8, overflow: 'hidden' }}>
                        <div
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
                                background: nested || arrVal ? 'var(--color-background-secondary)' : 'transparent',
                                borderBottom: (nested || arrVal) && !isCol ? '0.5px solid var(--color-border-tertiary)' : 'none',
                                cursor: nested || arrVal ? 'pointer' : 'default',
                            }}
                            onClick={() => (nested || arrVal) && toggle(key)}
                        >
                            <span style={{ fontSize: 13, fontWeight: nested || arrVal ? 500 : 400, flex: 1 }}>
                                {key}
                            </span>

                            {/* Hoja: input inline */}
                            {!nested && !arrVal && (
                                <div style={{ flex: 3 }} onClick={e => e.stopPropagation()}>
                                    <LeafEditor
                                        value={value === null ? '' : String(value)}
                                        onChange={val => onChange([...path, key], val)}
                                    />
                                </div>
                            )}

                            {arrVal && (
                                <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginRight: 4 }}>
                                    {value.length} ítems
                                </span>
                            )}
                            {nested && (
                                <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginRight: 4 }}>
                                    {Object.keys(value).length} campos
                                </span>
                            )}
                            {(nested || arrVal) && (isCol ? <ChevronDown size={14} /> : <ChevronUp size={14} />)}

                            <button
                                onClick={e => { e.stopPropagation(); removeKey(key); }}
                                style={{ padding: '2px 6px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
                            >
                                <Trash2 size={13} />
                            </button>
                        </div>

                        {!isCol && (nested || arrVal) && (
                            <div style={{ padding: '10px 14px' }}>
                                {arrVal && (
                                    <ArrayEditor value={value} onChange={val => onChange([...path, key], val)} />
                                )}
                                {nested && (
                                    <MapEditor data={value} path={[...path, key]} onChange={onChange} />
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            <button
                onClick={addKey}
                style={{ alignSelf: 'flex-start', fontSize: 12, padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 4 }}
            >
                <Plus size={12} /> Nuevo campo
            </button>
        </div>
    );
}

// ── Sección colapsable ────────────────────────────────────────────────────────
function Section({ titulo, children, defaultOpen = true }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div style={{ marginBottom: '1.25rem', border: '0.5px solid var(--color-border-tertiary)', borderRadius: 10, overflow: 'hidden' }}>
            <div
                onClick={() => setOpen(p => !p)}
                style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 16px', cursor: 'pointer',
                    background: 'var(--color-background-secondary)',
                    borderBottom: open ? '0.5px solid var(--color-border-tertiary)' : 'none',
                }}
            >
                <span style={{ fontWeight: 500, fontSize: 14 }}>{titulo}</span>
                {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </div>
            {open && <div style={{ padding: '14px 16px' }}>{children}</div>}
        </div>
    );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function SpecsDetalle() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [original, setOriginal] = useState(null);
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [dirty, setDirty] = useState(false);

    // ── Carga ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        const cargar = async () => {
            setLoading(true);
            try {
                const data = await obtenerEspecificacionesPorId(id);
                if (!data) {
                    toast.error('Ficha no encontrada');
                    navigate('/admin/specs');
                    return;
                }
                setOriginal(data);
                setForm(deepClone(data));
            } catch (e) {
                console.error(e);
                toast.error('Error cargando ficha');
                navigate('/admin/specs');
            } finally {
                setLoading(false);
            }
        };
        cargar();
    }, [id]); // eslint-disable-line

    // ── Actualizar campo por path ─────────────────────────────────────────────
    const handleChange = useCallback((path, val) => {
        setForm(prev => {
            const next = deepClone(prev);
            let cursor = next;
            for (let i = 0; i < path.length - 1; i++) {
                if (!cursor[path[i]] || typeof cursor[path[i]] !== 'object') cursor[path[i]] = {};
                cursor = cursor[path[i]];
            }
            const lastKey = path[path.length - 1];
            if (val === undefined) delete cursor[lastKey];
            else cursor[lastKey] = val;
            return next;
        });
        setDirty(true);
    }, []);

    // ── Campos de identificación (raíz + vehiculo.*) ──────────────────────────
    const handleIdent = (key, val) => {
        setForm(prev => {
            const next = deepClone(prev);
            next[key] = val;
            if (!next.vehiculo) next.vehiculo = {};
            next.vehiculo[key] = val;
            return next;
        });
        setDirty(true);
    };

    // ── Guardar ───────────────────────────────────────────────────────────────
    const guardar = async () => {
        setGuardando(true);
        try {
            await actualizarEspecificaciones(id, form);
            setOriginal(deepClone(form));
            setDirty(false);
            toast.success('Ficha guardada ✅');
        } catch (e) {
            console.error(e);
            toast.error('Error al guardar');
        } finally {
            setGuardando(false);
        }
    };

    const descartar = () => {
        if (!window.confirm('¿Descartar todos los cambios?')) return;
        setForm(deepClone(original));
        setDirty(false);
    };

    const handleVolver = () => {
        if (dirty && !window.confirm('Hay cambios sin guardar. ¿Salir de todas formas?')) return;
        navigate('/admin/specs');
    };

    // ── Render ────────────────────────────────────────────────────────────────
    if (loading) return (
        <div style={{ padding: '2rem', color: 'var(--color-text-secondary)' }}>Cargando ficha…</div>
    );
    if (!form) return null;

    // Calcular secciones "extras" (campos en specs.* no contemplados en SECCIONES_SPECS)
    const keysConocidos = new Set(SECCIONES_SPECS.map(s => s.key));
    const extrasSpecs = form.specs
        ? Object.entries(form.specs).filter(([k]) => !keysConocidos.has(k))
        : [];

    return (
        <div style={{ padding: '1.5rem', maxWidth: 900, margin: '0 auto' }}>

            {/* Barra superior */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                <button onClick={handleVolver} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <ArrowLeft size={14} /> Volver
                </button>
                <div style={{ flex: 1 }}>
                    <h1 style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>
                        {getMarca(form)} / {getModelo(form)}
                    </h1>
                    <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '2px 0 0' }}>
                        {getVersion(form) || 'sin versión'}
                        {form.metadata?.fuente && (
                            <span style={{ marginLeft: 10, opacity: 0.6 }}>· {form.metadata.fuente}</span>
                        )}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {dirty && (
                        <button onClick={descartar} style={{ fontSize: 13 }}>Descartar cambios</button>
                    )}
                    <button
                        onClick={guardar}
                        disabled={!dirty || guardando}
                        style={{
                            fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
                            color: dirty ? 'var(--color-text-success)' : 'var(--color-text-secondary)',
                            borderColor: dirty ? 'var(--color-border-success)' : 'var(--color-border-tertiary)',
                        }}
                    >
                        <Save size={14} /> {guardando ? 'Guardando…' : 'Guardar todo'}
                    </button>
                </div>
            </div>

            {/* Banner cambios sin guardar */}
            {dirty && (
                <div style={{
                    padding: '8px 14px', borderRadius: 8, marginBottom: '1rem',
                    background: 'var(--color-background-warning)',
                    border: '0.5px solid var(--color-border-warning)',
                    fontSize: 13, color: 'var(--color-text-warning)',
                }}>
                    Tienes cambios sin guardar.
                </div>
            )}

            {/* ── Identificación ── */}
            <Section titulo="Identificación">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                    {['marca', 'modelo', 'version'].map(key => (
                        <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', textTransform: 'capitalize' }}>{key}</span>
                            <input
                                value={form[key] || ''}
                                onChange={e => handleIdent(key, e.target.value)}
                                style={{ fontSize: 13, padding: '6px 10px' }}
                            />
                        </label>
                    ))}
                </div>
                {form.metadata && (
                    <div style={{ marginTop: 12, fontSize: 12, color: 'var(--color-text-secondary)', display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                        {form.metadata.fecha_extraccion && <span>Fecha extracción: {form.metadata.fecha_extraccion}</span>}
                        {form.metadata.versiones_en_documento && <span>Versiones en doc: {form.metadata.versiones_en_documento}</span>}
                        {form.metadata.notas && <span style={{ flex: '1 1 100%', fontStyle: 'italic' }}>{form.metadata.notas}</span>}
                    </div>
                )}
            </Section>

            {/* ── Secciones dinámicas de specs.* ── */}
            {form.specs && SECCIONES_SPECS.map(({ key, label }) => {
                const data = form.specs[key];
                if (!data) return null;
                return (
                    <Section key={key} titulo={label}>
                        {isArray(data) ? (
                            <ArrayEditor
                                value={data}
                                onChange={val => handleChange(['specs', key], val)}
                            />
                        ) : (
                            <MapEditor
                                data={data}
                                path={['specs', key]}
                                onChange={handleChange}
                            />
                        )}
                    </Section>
                );
            })}

            {/* ── Campos extras no contemplados ── */}
            {extrasSpecs.length > 0 && (
                <Section titulo="Otros campos" defaultOpen={false}>
                    <MapEditor
                        data={Object.fromEntries(extrasSpecs)}
                        path={['specs']}
                        onChange={handleChange}
                    />
                </Section>
            )}

            {/* ── Botón flotante guardar ── */}
            {dirty && (
                <div style={{ position: 'sticky', bottom: 16, display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                    <button
                        onClick={guardar}
                        disabled={guardando}
                        style={{
                            fontSize: 14, padding: '10px 24px',
                            display: 'flex', alignItems: 'center', gap: 8,
                            background: 'var(--color-background-success)',
                            color: 'var(--color-text-success)',
                            border: '0.5px solid var(--color-border-success)',
                            borderRadius: 8, cursor: 'pointer',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                        }}
                    >
                        <Save size={16} /> {guardando ? 'Guardando…' : 'Guardar todo'}
                    </button>
                </div>
            )}
        </div>
    );
}